/**
 * Transaction Filters Component
 * Search, transaction type, location, and date range filters
 * NO payment status filter (deprecated - separate accounting suite)
 */

import React, { useState } from 'react';
import {
  Box, Typography, Grid, Paper, TextField, InputAdornment,
  FormControl, Select, MenuItem, Button, Chip,
  useTheme, alpha
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { TRANSACTION_TYPES as ALL_TRANSACTION_TYPES } from './transactionConstants';
import LocationFilterDialogue from './LocationFilterDialogue';

// Only show these types in the filter dropdown
const TRANSACTION_TYPES = ALL_TRANSACTION_TYPES.filter(t =>
  ['inventory_creation', 'purchase', 'usage', 'transfer', 'adjustment'].includes(t.value)
);

const filterSelectSx = (theme) => ({
  height: 56,
  borderRadius: 3,
  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
  background: 'white',
  color: 'text.primary',
  fontWeight: 500,
  '&:hover': {
    border: `1px solid ${theme.palette.primary.main}`,
    boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
  },
  '&.Mui-focused': {
    border: `1px solid ${theme.palette.primary.main}`,
    boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`
  },
  '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
  '&:hover .MuiOutlinedInput-notchedOutline': { border: 'none' },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { border: 'none' }
});

const datePickerSx = (theme) => ({
  '& .MuiOutlinedInput-root': {
    height: 56,
    borderRadius: 3,
    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
    background: 'white',
    '&:hover': {
      border: `1px solid ${theme.palette.primary.main}`,
      boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
    },
    '&.Mui-focused': {
      border: `1px solid ${theme.palette.primary.main}`,
      boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`
    },
    '& fieldset': { border: 'none' },
    '&:hover fieldset': { border: 'none' },
    '&.Mui-focused fieldset': { border: 'none' }
  },
  '& .MuiInputBase-input': { fontSize: '1rem', fontWeight: 500, py: 2, px: 2 }
});

export default function Filters({
  searchQuery, onSearchChange,
  selectedType, onTypeChange,
  selectedLocation, onLocationChange,
  fromDate, onFromDateChange,
  toDate, onToDateChange,
  locations
}) {
  const theme = useTheme();
  const [locationDialogOpen, setLocationDialogOpen] = useState(false);

  const hasActiveFilters = searchQuery || selectedType || selectedLocation || fromDate || toDate;

  return (
    <>
      <Paper sx={{
        p: 4,
        mb: 4,
        borderRadius: 3,
        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
        background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
        boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.25)}`,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
        }
      }}>
        <Grid container spacing={3} alignItems="flex-end">
          {/* Search */}
          <Grid item xs={12} md={3.5}>
            <Box>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: 'text.secondary', fontSize: '0.9rem' }}>
                Search
              </Typography>
              <TextField
                fullWidth
                placeholder="Search transactions..."
                value={searchQuery}
                onChange={(e) => onSearchChange(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: theme.palette.primary.main }} />
                    </InputAdornment>
                  ),
                  sx: {
                    borderRadius: 3,
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                    background: 'white',
                    height: 56,
                    '&:hover': {
                      border: `1px solid ${theme.palette.primary.main}`,
                      boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                    },
                    '&.Mui-focused': {
                      border: `1px solid ${theme.palette.primary.main}`,
                      boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.25)}`
                    }
                  }
                }}
                sx={{
                  '& .MuiInputBase-input': { fontSize: '1rem', fontWeight: 500, py: 2, px: 2 },
                  '& .MuiOutlinedInput-root': {
                    height: 56,
                    '& fieldset': { border: 'none' },
                    '&:hover fieldset': { border: 'none' },
                    '&.Mui-focused fieldset': { border: 'none' }
                  }
                }}
                size="medium"
              />
            </Box>
          </Grid>

          {/* Transaction Type */}
          <Grid item xs={12} md={2.5}>
            <Box>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: 'text.secondary', fontSize: '0.9rem' }}>
                Transaction Type
              </Typography>
              <FormControl fullWidth size="medium">
                <Select
                  value={selectedType}
                  onChange={(e) => onTypeChange(e.target.value)}
                  displayEmpty
                  sx={filterSelectSx(theme)}
                >
                  <MenuItem value="">All Types</MenuItem>
                  {TRANSACTION_TYPES.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {type.icon}
                        {type.label}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Grid>

          {/* Location */}
          <Grid item xs={12} md={2}>
            <Box>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: 'text.secondary', fontSize: '0.9rem' }}>
                Location
              </Typography>
              <Button
                fullWidth
                onClick={() => setLocationDialogOpen(true)}
                variant="outlined"
                sx={{
                  height: 56,
                  borderRadius: 3,
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  background: 'white',
                  color: 'text.primary',
                  fontWeight: 500,
                  justifyContent: 'flex-start',
                  textTransform: 'none',
                  '&:hover': {
                    border: `1px solid ${theme.palette.primary.main}`,
                    boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                  }
                }}
              >
                {selectedLocation
                  ? locations.find(l => l.id === selectedLocation)?.name || 'Select Location'
                  : 'All Locations'
                }
              </Button>
            </Box>
          </Grid>

          {/* From Date */}
          <Grid item xs={12} md={2}>
            <Box>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: 'text.secondary', fontSize: '0.9rem' }}>
                From Date
              </Typography>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  value={fromDate ? dayjs(fromDate) : null}
                  maxDate={toDate ? dayjs(toDate) : undefined}
                  onChange={(val) => onFromDateChange(val ? val.format('YYYY-MM-DD') : '')}
                  slotProps={{ textField: { fullWidth: true, size: 'medium', sx: datePickerSx(theme) } }}
                />
              </LocalizationProvider>
            </Box>
          </Grid>

          {/* To Date */}
          <Grid item xs={12} md={2}>
            <Box>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: 'text.secondary', fontSize: '0.9rem' }}>
                To Date
              </Typography>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  value={toDate ? dayjs(toDate) : null}
                  minDate={fromDate ? dayjs(fromDate) : undefined}
                  onChange={(val) => onToDateChange(val ? val.format('YYYY-MM-DD') : '')}
                  slotProps={{ textField: { fullWidth: true, size: 'medium', sx: datePickerSx(theme) } }}
                />
              </LocalizationProvider>
            </Box>
          </Grid>
        </Grid>

        {/* Active Filter Chips */}
        {hasActiveFilters && (
          <Grid container spacing={3} sx={{ mt: 2 }} alignItems="center">
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                {(fromDate || toDate) && (
                  <Chip
                    label={`${fromDate ? dayjs(fromDate).format('MMM DD, YYYY') : 'Start'} - ${toDate ? dayjs(toDate).format('MMM DD, YYYY') : 'End'}`}
                    onDelete={() => { onFromDateChange(''); onToDateChange(''); }}
                    color="primary"
                    variant="outlined"
                    sx={{ border: '2px solid', borderColor: 'primary.main', fontWeight: 500 }}
                  />
                )}
                {searchQuery && (
                  <Chip
                    label={`Search: "${searchQuery}"`}
                    onDelete={() => onSearchChange('')}
                    color="secondary"
                    variant="outlined"
                    sx={{ border: '2px solid', borderColor: 'secondary.main', fontWeight: 500 }}
                  />
                )}
                {selectedType && (
                  <Chip
                    label={`Type: ${TRANSACTION_TYPES.find(t => t.value === selectedType)?.label || selectedType}`}
                    onDelete={() => onTypeChange('')}
                    color="info"
                    variant="outlined"
                    sx={{ border: '2px solid', borderColor: 'info.main', fontWeight: 500 }}
                  />
                )}
                {selectedLocation && (
                  <Chip
                    label={`Location: ${locations.find(l => l.id === selectedLocation)?.name || 'Unknown'}`}
                    onDelete={() => onLocationChange('')}
                    color="warning"
                    variant="outlined"
                    sx={{ border: '2px solid', borderColor: 'warning.main', fontWeight: 500 }}
                  />
                )}
              </Box>
            </Grid>
          </Grid>
        )}
      </Paper>

      <LocationFilterDialogue
        open={locationDialogOpen}
        onClose={() => setLocationDialogOpen(false)}
        locations={locations}
        selectedLocation={selectedLocation}
        onApply={(locationId) => {
          onLocationChange(locationId);
          setLocationDialogOpen(false);
        }}
      />
    </>
  );
}
